import { useState, useMemo } from 'react';
import { Goal } from '../services/goalService';
import { calculateGoalCompletionPercentage } from '../utils/goalProgress';
import { compareLastUpdated } from '../utils/goalOrder';

/** `updated` (the default, T-118) puts the last changed goal first; `date` is the creation date. */
export type GoalsSortBy = 'updated' | 'name' | 'progress' | 'date' | 'priority';

interface UseGoalsPageReturn {
  filteredGoals: Goal[];
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  sortBy: GoalsSortBy;
  setSortBy: (sort: GoalsSortBy) => void;
  filterBy: 'all' | 'favorites' | 'completed' | 'active';
  setFilterBy: (filter: 'all' | 'favorites' | 'completed' | 'active') => void;
  isGridView: boolean;
  setIsGridView: (isGrid: boolean) => void;
}

export const useGoalsPage = (goals: Goal[]): UseGoalsPageReturn => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<GoalsSortBy>('updated');
  const [filterBy, setFilterBy] = useState<'all' | 'favorites' | 'completed' | 'active'>('all');
  const [isGridView, setIsGridView] = useState(true);

  const filteredGoals = useMemo(() => {
    let filtered = [...goals];

    // Search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(goal => 
        goal.title.toLowerCase().includes(term) ||
        goal.description?.toLowerCase().includes(term) ||
        goal.subGoals?.some(sg => 
          sg.title.toLowerCase().includes(term) ||
          sg.description?.toLowerCase().includes(term)
        )
      );
    }

    // Status filter
    switch (filterBy) {
      case 'favorites':
        filtered = filtered.filter(goal => goal.isFavorite);
        break;
      case 'completed':
        filtered = filtered.filter(goal => goal.completed);
        break;
      case 'active':
        filtered = filtered.filter(goal => !goal.completed);
        break;
    }

    // Sort
    switch (sortBy) {
      case 'updated':
        filtered.sort(compareLastUpdated);
        break;
      case 'name':
        filtered.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case 'progress':
        filtered.sort((a, b) => {
          return calculateGoalCompletionPercentage(b) - calculateGoalCompletionPercentage(a);
        });
        break;
      case 'date':
        filtered.sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
        break;
      case 'priority':
        // Since Goal doesn't have priority, sort by favorite status instead
        filtered.sort((a, b) => {
          if (a.isFavorite && !b.isFavorite) return -1;
          if (!a.isFavorite && b.isFavorite) return 1;
          return a.title.localeCompare(b.title);
        });
        break;
    }

    return filtered;
  }, [goals, searchTerm, sortBy, filterBy]);

  return {
    filteredGoals,
    searchTerm,
    setSearchTerm,
    sortBy,
    setSortBy,
    filterBy,
    setFilterBy,
    isGridView,
    setIsGridView
  };
};
